/* Paramètres › Organisation (v3.3 WF-042; ruling « Organisation », 19/10, and the paper ruling P1–P8).
   WF-042's frame for ONE card, « Étiquettes »: the reference's « Catégories » and « Emplacements » are ruled out (one
   concept; a support's `place` stays free text). The workspace's list with its counts (campaigns, supports — archived
   and deactivated items included, P3: the number « Supprimer » and « Fusionner » act on, in the reader's scope),
   « Nouvelle étiquette », « Renommer », « Fusionner » (choose the survivor: every item is re-pointed, duplicates fold),
   « Supprimer » (a dialog names how many items lose it), the cap at 30, the guardrail on the name. Written by the owner,
   an administrator and a manager over every establishment (the library's rule, lib/tags › canWriteTagList); a manager
   of some establishments and staff read. No plan gate. An étiquette never reaches a customer.
   With an API (lib-pack-21) the list and the items' étiquettes are the server's (lib/tags over lib/writes): a dialog closes
   once the server has taken its write; a refusal stays in the dialog in the app's words, and the list is read back.
   Point 6 (wireframes-fully-ruling § 6, lib-pack-23): WF-042's « Catégories » (one per campaign) and « Emplacements » (one
   per support) come back beside « Étiquettes », in WF-042's order, as OrganisationListe cards over lib/organisation. While
   organisationReady() is false (with an API, until 0039 is on staging) they draw nothing: the page is the étiquettes' alone. */
import { useState } from 'react'
import { Button, Card, Field, Input, Modal, Note, PageHeader, Select } from '@/ui'
import { useCampaigns, useLocations, useSupports } from '@/lib/api'
import { allCampaigns, mergeSupports } from '@/lib/campaign'
import { currentUser, isScoped } from '@/lib/scope'
import { editRefusalLines, settled, writeMark } from '@/lib/writes'
import {
  addTag, canWriteTagList, cleanTagName, getTags, mergeTag, setTags, tagCounts, tagIssues, useTags,
  MAX_TAGS, TAG_CAP_LINE, type Tag,
} from '@/lib/tags'
import {
  addCategory, addPlace, canWriteCategoryList, canWritePlaceList, categoryCounts, categoryIssues, cleanOrgName, getCategories,
  getPlaces, mergeCategory, mergePlace, organisationReady, placeCounts, placeIssues, setCategories, setPlaces, useCategories,
  useOrganisationStore, usePlaces, CATEGORY_CAP_LINE, CATEGORY_ROLE_LINE, MAX_CATEGORIES, MAX_PLACES, PLACE_CAP_LINE, PLACE_ROLE_LINE,
} from '@/lib/organisation'
import { OrganisationListe } from './OrganisationListe'

export const ORGANISATION_READ_ONLY = 'Vous consultez les étiquettes : elles se gèrent par le propriétaire, un administrateur ou un manager de tous les établissements.'
const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`
const countLine = (c: { campaigns: number; supports: number }) => `${plural(c.campaigns, 'campagne', 'campagnes')} · ${plural(c.supports, 'support', 'supports')}`
const countWords = (c: { campaigns: number; supports: number }) => `${plural(c.campaigns, 'campagne', 'campagnes')} et ${plural(c.supports, 'support', 'supports')}`

type Dialog = { kind: 'new' } | { kind: 'rename'; tag: Tag } | { kind: 'merge'; tag: Tag } | { kind: 'delete'; tag: Tag } | null

export default function Organisation() {
  const tags = useTags()
  const categories = useCategories()
  const places = usePlaces()
  useOrganisationStore()   /* re-renders on any item's value (the counts) and when the lists arrive */
  const ready = organisationReady()
  const { data: locations } = useLocations()
  const { data: apiCampaigns } = useCampaigns()
  const { data: apiSupports } = useSupports()
  const [dialog, setDialog] = useState<Dialog>(null)
  const [name, setName] = useState('')
  const [into, setInto] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const all = locations ?? []
  const writer = canWriteTagList(all.map(l => l.id))
  /* P3 · the reader's scope: a manager or staff counts their establishments' items; no status is left out */
  const u = currentUser()
  const mine = (locs: string[]) => !isScoped(u) || locs.some(l => u.locations.includes(l))
  const names = Object.fromEntries(all.map(l => [l.id, l.name]))
  const campaignIds = allCampaigns(apiCampaigns, names).filter(c => mine(c.locations)).map(c => c.id)
  const supportIds = mergeSupports(apiSupports).filter(s => mine([s.locationId])).map(s => s.id)
  const counts = tagCounts(campaignIds, supportIds)
  const zero = { campaigns: 0, supports: 0 }
  const full = tags.length >= MAX_TAGS
  const allIds = all.map(l => l.id)

  const close = () => { setDialog(null); setName(''); setInto(''); setError(null); setBusy(false) }
  /** the 2b pattern: at once in the demo; with an API, once every write asked since `mark` has answered */
  const after = (mark: number) => { setBusy(true); void settled(mark).then(no => { if (no) { setBusy(false); setError(editRefusalLines(no)[0]) } else close() }) }
  const open = (d: NonNullable<Dialog>) => {
    setError(null)
    setName(d.kind === 'rename' ? d.tag.name : '')
    setInto(d.kind === 'merge' ? (tags.find(t => t.id !== d.tag.id)?.id ?? '') : '')
    setDialog(d)
  }
  const create = () => { if (busy) return; const mark = writeMark(); const r = addTag(name); if (typeof r === 'string') { setError(r); return } after(mark) }
  const rename = (t: Tag) => {
    if (busy) return
    const issue = tagIssues(name, getTags(), t.id)
    if (issue) { setError(issue); return }
    const mark = writeMark()
    setTags(getTags().map(x => (x.id === t.id ? { ...x, name: cleanTagName(name) } : x)))
    after(mark)
  }
  const merge = (t: Tag) => { if (busy || !into || into === t.id) return; const mark = writeMark(); mergeTag(t.id, into); after(mark) }
  const remove = (t: Tag) => { if (busy) return; const mark = writeMark(); setTags(getTags().filter(x => x.id !== t.id)); after(mark) }
  const refused = error && <p className="f-help err" role="alert" style={{ margin: '8px 0 0' }} data-testid="organisation-refus">{error}</p>

  const d = dialog
  const target = d && d.kind === 'merge' ? tags.find(t => t.id === into) : undefined
  return (
    <section className="page" data-frame="WF-042" data-testid="organisation" style={{ maxWidth: 980 }}>
      <PageHeader title="Organisation" description="Des repères internes, sans effet sur le parcours client." />
      {!writer && <p className="f-help" style={{ margin: 0 }} data-testid="lecture-seule">{ORGANISATION_READ_ONLY}</p>}
      <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', alignItems: 'start' }}>
        {ready && <OrganisationListe kind="categories" one="categorie" title="Catégories" help="Une catégorie facultative par campagne."
          newLabel="Nouvelle catégorie" nameLabel="Nom de la catégorie" placeholder="Ex. Fidélisation" intoLabel="Dans la catégorie"
          unit={['campagne', 'campagnes']} thisOne="cette catégorie"
          deleteEffect={what => `Elle est retirée de ${what}. Les campagnes restent telles quelles, sans catégorie.`}
          capCount={n => `${n} sur ${MAX_CATEGORIES} catégories.`}
          items={categories} counts={categoryCounts(campaignIds)} max={MAX_CATEGORIES} capLine={CATEGORY_CAP_LINE} roleLine={CATEGORY_ROLE_LINE}
          writer={canWriteCategoryList(allIds)} issues={categoryIssues} add={addCategory} setList={setCategories} merge={mergeCategory}
          clean={cleanOrgName} getList={getCategories} />}
        <Card data-testid="etiquettes-carte">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <h2 className="t-section" style={{ margin: 0, flex: 1 }}>Étiquettes</h2>
            {writer && <Button size="sm" variant="primary" disabled={full} onClick={() => open({ kind: 'new' })}>Nouvelle étiquette</Button>}
          </div>
          <p className="f-help" style={{ margin: '8px 0 12px' }}>Plusieurs repères communs, comme Automne 2026.</p>
          {tags.length === 0
            ? <p className="f-help" style={{ margin: 0 }} data-testid="etiquettes-vide">Aucun terme créé.</p>
            : <div className="list" data-testid="etiquettes-liste">
              {tags.map(t => (
                <div key={t.id} className="listrow" data-testid="etiquette-ligne" data-tag={t.name} style={{ flexWrap: 'wrap' }}>
                  <div className="listrow-main" style={{ minWidth: 0, flex: '1 1 180px' }}>
                    <span className="listrow-title">{t.name}</span>
                    <span className="listrow-meta" data-testid="etiquette-compte">{countLine(counts[t.id] ?? zero)}</span>
                  </div>
                  {writer && <span className="listrow-end" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button size="sm" onClick={() => open({ kind: 'rename', tag: t })}>Renommer</Button>
                    <Button size="sm" disabled={tags.length < 2} onClick={() => open({ kind: 'merge', tag: t })}>Fusionner</Button>
                    <Button size="sm" variant="quiet" onClick={() => open({ kind: 'delete', tag: t })}>Supprimer</Button>
                  </span>}
                </div>
              ))}
            </div>}
          <p className="f-help" style={{ margin: '12px 0 0' }} data-testid="etiquettes-plafond">{full ? TAG_CAP_LINE : `${tags.length} sur ${MAX_TAGS} étiquettes.`}</p>
        </Card>
        {ready && <OrganisationListe kind="emplacements" one="emplacement" title="Emplacements" help="Des endroits à l’intérieur de l’établissement."
          newLabel="Nouvel emplacement" nameLabel="Nom de l’emplacement" placeholder="Ex. Comptoir" intoLabel="Dans l’emplacement"
          unit={['support', 'supports']} thisOne="cet emplacement"
          deleteEffect={what => `Il est retiré de ${what}. Les supports restent tels quels, sans emplacement.`}
          capCount={n => `${n} sur ${MAX_PLACES} emplacements.`}
          items={places} counts={placeCounts(supportIds)} max={MAX_PLACES} capLine={PLACE_CAP_LINE} roleLine={PLACE_ROLE_LINE}
          writer={canWritePlaceList(allIds)} issues={placeIssues} add={addPlace} setList={setPlaces} merge={mergePlace}
          clean={cleanOrgName} getList={getPlaces} />}
      </div>
      <Note tone="ok">Renommer ou fusionner conserve les campagnes et les supports étiquetés. Les étiquettes ne sont jamais obligatoires, et vos clients ne les voient pas.</Note>

      <Modal open={d?.kind === 'new'} title="Nouvelle étiquette" onClose={close}
        actions={<><Button onClick={close}>Annuler</Button><Button variant="primary" disabled={busy} onClick={create}>Créer</Button></>}>
        <Field label="Nom" error={error ?? undefined}>{id => <Input id={id} aria-label="Nom de l’étiquette" value={name} autoFocus placeholder="Ex. Automne 2026"
          onChange={e => { setName(e.target.value); setError(null) }} onKeyDown={e => { if (e.key === 'Enter') create() }} />}</Field>
      </Modal>
      {d?.kind === 'rename' && <Modal open title={`Renommer « ${d.tag.name} »`} onClose={close}
        actions={<><Button onClick={close}>Annuler</Button><Button variant="primary" disabled={busy} onClick={() => rename(d.tag)}>Renommer</Button></>}>
        <Field label="Nouveau nom" error={error ?? undefined}>{id => <Input id={id} aria-label="Nouveau nom" value={name} autoFocus
          onChange={e => { setName(e.target.value); setError(null) }} onKeyDown={e => { if (e.key === 'Enter') rename(d.tag) }} />}</Field>
        <p className="f-help" style={{ margin: '8px 0 0' }}>{countWords(counts[d.tag.id] ?? zero)} gardent cette étiquette sous son nouveau nom.</p>
      </Modal>}
      {d?.kind === 'merge' && <Modal open title={`Fusionner « ${d.tag.name} »`} onClose={close}
        actions={<><Button onClick={close}>Annuler</Button><Button variant="primary" disabled={!target || busy} onClick={() => merge(d.tag)}>Fusionner</Button></>}>
        <Field label="Dans l’étiquette">{id => <Select id={id} aria-label="Dans l’étiquette" value={into} onChange={e => setInto(e.target.value)}>
          {tags.filter(t => t.id !== d.tag.id).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>}</Field>
        <p className="t-body" style={{ margin: '8px 0 0' }} data-testid="fusion-effet">{target ? `${countWords(counts[d.tag.id] ?? zero)} passent sous « ${target.name} », qui reste. « ${d.tag.name} » disparaît.` : ''}</p>
        {refused}
      </Modal>}
      {d?.kind === 'delete' && <Modal open title={`Supprimer « ${d.tag.name} » ?`} onClose={close}
        actions={<><Button onClick={close}>Annuler</Button><Button variant="danger" data-testid="supprimer-ok" disabled={busy} onClick={() => remove(d.tag)}>Supprimer</Button></>}>
        <p className="t-body" style={{ margin: 0 }} data-testid="suppression-effet">{`Elle est retirée de ${countWords(counts[d.tag.id] ?? zero)}. Les campagnes et les supports restent tels quels.`}</p>
        {refused}
      </Modal>}
    </section>
  )
}
